import { DOCUMENT } from '@angular/common';
import { Directive, inject, signal } from '@angular/core';

@Directive({
  selector: '[appFullscreen]',
  exportAs: 'appFullscreen',
  host: {
    '[class.app-fullscreen]': 'active()',
    '(document:fullscreenchange)': 'onFullscreenChange()',
  },
})
// Lets one part of a screen take over the whole monitor - a plan board, a dashboard - with the
// menus out of the way. Production plan, 2026-10-27 (Steve: "a full screen option").
//
//   <div appFullscreen #fullscreen="appFullscreen"> ... <button (click)="fullscreen.toggle()">
//
// Two things happen together, on purpose:
//   - the element is laid over the page (the app-fullscreen class in styles/_layout.scss), and
//   - the browser goes full screen on the DOCUMENT, not the element.
// Making the element itself the browser's full-screen element would hide everything outside
// it - toasts, the help panel, any dialog - because those are attached to the body. Full
// screen on the document keeps them, and the overlay keeps the menus out of sight.
// Esc leaves the browser's full screen, and the overlay follows it.
export class FullscreenDirective {
  private readonly document = inject(DOCUMENT);

  public readonly active = signal(false);

  toggle() {
    if (this.active()) {
      this.exit();
    } else {
      this.enter();
    }
  }

  enter() {
    this.active.set(true);
    // Refused in some contexts (an iframe, a browser setting) - the overlay still works alone.
    this.document.documentElement.requestFullscreen?.().catch(() => undefined);
  }

  exit() {
    this.active.set(false);
    if (this.document.fullscreenElement) {
      this.document.exitFullscreen?.().catch(() => undefined);
    }
  }

  protected onFullscreenChange() {
    // Leaving the browser's full screen (Esc, F11) takes the overlay with it.
    if (!this.document.fullscreenElement && this.active()) {
      this.active.set(false);
    }
  }
}
